import * as styles from "./AlphaIndex.css";

import { useAtomValue } from "jotai";
import { useEffect, useRef, useState } from "react";

import { useAddressBookActions } from "../../hooks/useAddressBookActions";
import { useContactSections } from "../../hooks/useContactSections";
import { filteredContactsAtom } from "../../stores";
import { AlphaIndexArrow } from "./AlphaIndexArrow";

const ALPHABET = [...Array(26)].map((_, i) => String.fromCharCode(65 + i));

type Index = { disabled: boolean; letter: string };

export function AlphaIndex() {
  const { scrollToLetter } = useAddressBookActions();

  const contacts = useAtomValue(filteredContactsAtom);

  const sections = useContactSections(contacts);

  const indicesRef = useRef<HTMLDivElement>(null);

  const [scrollState, setScrollState] = useState({
    up: false,
    down: false,
    compact: false,
  });

  const [isDragging, setIsDragging] = useState(false);

  const scrollIntervalRef = useRef<number | null>(null);

  // Create indices with disabled state based on sections
  const indices: Index[] = [...ALPHABET, "#"].map((letter) => ({
    letter,
    disabled: !sections[letter],
  }));

  const hasActive = indices.some((index) => !index.disabled);

  const updateScrollState = () => {
    if (!indicesRef.current) {
      return;
    }

    const { scrollTop, scrollHeight, clientHeight } = indicesRef.current;

    setScrollState({
      up: scrollTop > 0,
      down: scrollTop + clientHeight < scrollHeight - 1,
      compact: scrollHeight > clientHeight,
    });
  };

  const scrollUp = () => {
    indicesRef.current?.scrollBy({ top: -100, behavior: "smooth" });
  };

  const scrollDown = () => {
    indicesRef.current?.scrollBy({ top: 100, behavior: "smooth" });
  };

  const startAutoScroll = (direction: "up" | "down") => {
    if (scrollIntervalRef.current) {
      return;
    }

    const amount = direction === "up" ? -20 : 20;

    scrollIntervalRef.current = window.setInterval(() => {
      indicesRef.current?.scrollBy({ top: amount });
    }, 50);
  };

  const stopAutoScroll = () => {
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
      scrollIntervalRef.current = null;
    }
  };

  const handleArrowEnter = (direction: "up" | "down") => () => {
    if (isDragging) {
      startAutoScroll(direction);
    }
  };

  const handleArrowLeave = () => {
    stopAutoScroll();
  };

  const handleTouchStart = (letter: string) => (e: React.TouchEvent) => {
    e.preventDefault();
    setIsDragging(true);
    scrollToLetter(letter);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) {
      return;
    }

    const touch = e.touches[0];
    const element = document.elementFromPoint(touch.clientX, touch.clientY);
    const letter = element?.textContent;

    if (letter && indices.some((idx) => idx.letter === letter)) {
      scrollToLetter(letter);
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  const handleMouseDown = (letter: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    scrollToLetter(letter);
  };

  const handleMouseEnter = (letter: string) => () => {
    if (isDragging) {
      scrollToLetter(letter);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    stopAutoScroll();
  };

  // Stop auto-scroll if mouse is released outside of component
  useEffect(() => {
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mouseup", handleMouseUp);
      stopAutoScroll();
    };
  }, []);

  // Update scroll state on mount and when sections change
  useEffect(() => {
    const resizeObserver = new ResizeObserver(updateScrollState);

    if (indicesRef.current) {
      resizeObserver.observe(indicesRef.current);
      updateScrollState();
    }

    return () => resizeObserver.disconnect();
  }, []);

  return (
    <div className={styles.alphaIndex}>
      <AlphaIndexArrow
        direction="up"
        onClick={scrollUp}
        onMouseEnter={handleArrowEnter("up")}
        onMouseLeave={handleArrowLeave}
        state={scrollState}
        enabled={hasActive}
      />

      <div
        ref={indicesRef}
        className={styles.indices}
        onScroll={updateScrollState}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {indices.map((index) => (
          <a
            key={index.letter}
            className={styles.link({ disabled: index.disabled })}
            href="#"
            onMouseDown={handleMouseDown(index.letter)}
            onMouseEnter={handleMouseEnter(index.letter)}
            onTouchStart={handleTouchStart(index.letter)}
          >
            {index.letter}
          </a>
        ))}
      </div>

      <AlphaIndexArrow
        direction="down"
        onClick={scrollDown}
        onMouseEnter={handleArrowEnter("down")}
        onMouseLeave={handleArrowLeave}
        state={scrollState}
        enabled={hasActive}
      />
    </div>
  );
}

// class Indices implements Iterable<Index> {
//   readonly items: Index[];

//   constructor() {
//     this.items = reactive(
//       [...ALPHABET, "#"].map((letter) => ({
//         letter,
//         disabled: true,
//       })),
//     );
//   }

//   *[Symbol.iterator](): Iterator<Index> {
//     yield* this.items;
//   }

//   get length() {
//     return this.items.length;
//   }

//   map<T>(fn: (index: Index, i: number) => T): T[] {
//     return this.items.map(fn);
//   }

//   includes(letter: string): boolean {
//     return this.items.some((index) => index.letter === letter);
//   }

//   hasActive() {
//     return van.derive(() => this.items.some((index) => !index.disabled));
//   }
// }
